import { addToRecord, arrayWithoutSubset, updateRecord } from '@ms/yammer-data-support';
import { BroadcastStatus } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type { CancelEventFulfilledAction } from '../../domains/cancelEvent/actions.types';
import type { LiveEventLoadByGroupIdAction as EventLoadByGroupIdAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import type { LiveEventStatusUpdateAction as EventStatusUpdateAction } from '../../domains/liveEventsStatuses/actions.types';
import type {
  LiveEvent as Event,
  EventEdge,
  LiveEventStatusInformation as EventStatusInformation,
  Group,
} from '../types';

import type { CommunityEvents, CommunityEventsDisplaySection, EventsInGroup } from './types';

export type CommunityEventsAction = CancelEventFulfilledAction | EventLoadByGroupIdAction | EventStatusUpdateAction;

const initialState: CommunityEvents = {};

const stateWithMovedEdge = (
  state: CommunityEvents,
  communityId: keyof CommunityEvents,
  broadcastId: EventEdge['broadcastId'],
  target: keyof EventsInGroup
) => {
  if (!state[communityId]) {
    return state;
  }

  const { currentEvents, futureEvents, pastEvents } = state[communityId];
  const edgeToMove = [...currentEvents.edges, ...futureEvents.edges, ...pastEvents.edges].find(
    (edge) => edge.broadcastId === broadcastId
  );

  if (!edgeToMove) {
    return state;
  }

  return updateRecord(state, communityId, {
    currentEvents: {
      ...currentEvents,
      edges: arrayWithoutSubset(currentEvents.edges, [edgeToMove]),
    },
    futureEvents: {
      ...futureEvents,
      edges: arrayWithoutSubset(futureEvents.edges, [edgeToMove]),
    },
    pastEvents: {
      ...pastEvents,
      edges: arrayWithoutSubset(pastEvents.edges, [edgeToMove]),
    },
    [target]: {
      ...state[communityId][target],
      edges: [edgeToMove, ...arrayWithoutSubset(state[communityId][target].edges, [edgeToMove])],
    },
  });
};

const updateByEventStatusInformation = (
  state: CommunityEvents,
  { broadcastId, groupId: communityId, status }: EventStatusInformation
) => {
  switch (status) {
    case BroadcastStatus.STARTED:
      return stateWithMovedEdge(state, communityId, broadcastId, 'currentEvents');
    case BroadcastStatus.ENDED:
      return stateWithMovedEdge(state, communityId, broadcastId, 'pastEvents');
    default:
      return state;
  }
};

const eventsDisplaySectionWithoutEvent = (
  eventsDisplaySection: CommunityEventsDisplaySection,
  broadcastId: Event['broadcastId']
): CommunityEventsDisplaySection => {
  const edges = eventsDisplaySection.edges.filter((edge) => edge.broadcastId !== broadcastId);

  return {
    ...eventsDisplaySection,
    edges,
  };
};

const stateWithoutEventInGroup = (state: CommunityEvents, broadcastId: Event['broadcastId'], groupId: Group['id']) => {
  if (!state[groupId]) {
    return state;
  }

  const { currentEvents, futureEvents, pastEvents } = state[groupId];

  const eventsInGroup: EventsInGroup = {
    currentEvents: eventsDisplaySectionWithoutEvent(currentEvents, broadcastId),
    futureEvents: eventsDisplaySectionWithoutEvent(futureEvents, broadcastId),
    pastEvents: eventsDisplaySectionWithoutEvent(pastEvents, broadcastId),
  };

  return {
    ...state,
    [groupId]: eventsInGroup,
  };
};

const reducer: Reducer<CommunityEvents, CommunityEventsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CANCEL_EVENT.FULFILLED':
      return stateWithoutEventInGroup(state, action.payload.broadcastId, action.payload.groupId);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return addToRecord(state, action.payload.groupId, {
        currentEvents: action.payload.currentEventsDisplay,
        futureEvents: action.payload.futureEventsDisplay,
        pastEvents: action.payload.pastEventsDisplay,
      });
    case 'LIVE_EVENT_STATUS.UPDATE':
      return updateByEventStatusInformation(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
